import {AxiosInstance, InternalAxiosRequestConfig} from "axios";
import i18n from "@/i18n";

/**
 * Builds the query params for an outgoing request, adding `lang` set to `currentLng` — but only when
 * the caller has not already supplied a `lang` param explicitly. An existing `lang` param always wins
 * (e.g. a request already carrying an explicit `?lang=` is never silently overridden). Returns a new
 * object — never mutates `existingParams` (project convention: spread for immutability).
 */
export function buildLangParam(
    currentLng: string,
    existingParams: Record<string, unknown> | undefined
): Record<string, unknown> {
    if (existingParams && Object.hasOwn(existingParams, "lang")) {
        return existingParams;
    }
    return {...existingParams, lang: currentLng};
}

/**
 * Registers a request interceptor on the given Axios instance that tags every outgoing request with
 * the current i18next language via a `lang` query parameter (see `buildLangParam`). Anonymous backend
 * calls (login failure, password-reset request, external order tracking) thereby carry the resolved
 * anonymous language for the backend `LocaleResolver` to pick up; authenticated calls carry the
 * profile language (`lang` already equals the current session's language in that case, so behavior for
 * logged-in users is unchanged).
 */
export function registerLangInterceptor(instance: AxiosInstance): void {
    instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        config.params = buildLangParam(i18n.language, config.params as Record<string, unknown> | undefined);
        return config;
    });
}
